import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { get } from "lodash";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DataTableService } from "../../../shared/components/data-table/data-table.service";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { yesNoOptions } from "../../../shared/constants/components";
import { pattern } from "../../../shared/constants/patterns";
import { BoolToStringPipe } from "../../../shared/pipes/bool-to-string/bool-to-string.pipe";
import { UserInfo } from "../../../shared/types/user-info";
import { getMaintenanceProviderSearchFilterOptions } from "./maintenance-provider";
import { MaintenanceProviderService } from "./maintenance-provider.service";
import { MaintenanceProvider } from "./maintenance-provider.types";

@Injectable({
  providedIn: "root",
})
export class MaintenanceProviderTableService {
  currentUser: UserInfo;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private maintenanceProviderService: MaintenanceProviderService,
    private dataTableService: DataTableService<MaintenanceProvider>,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  /** Get table config */
  getMaintenanceProviderTableConfig = (config?: DataTableConfig): DataTable<MaintenanceProvider> => {
    const { hidden, shown, actionColumn } = config || {};

    const columns: DataTableColumn<MaintenanceProvider>[] = (
      [
        {
          prop: "name",
          name: "Name",
          link: {
            path: (maintenanceProvider) => `/maintenance-providers/${maintenanceProvider.id}`,
          },
          inlineEdit: {
            control: "input",
            validators: () => [Validators.minLength(1), Validators.maxLength(255), Validators.pattern(pattern.words)],
          },
        },
        {
          prop: "email",
          name: "Email",
          inlineEdit: {
            control: "input",
            required: false,
            validators: () => [Validators.maxLength(255), Validators.pattern(pattern.email)],
          },
        },
        {
          prop: "phone",
          name: "Phone",
          inlineEdit: {
            control: "input",
            required: false,
            validators: () => [Validators.maxLength(255)],
          },
        },
        {
          prop: "isChannelWorks",
          name: "Is Staff Managed",
          pipes: [{ clazz: BoolToStringPipe }],
          inlineEdit: {
            control: "select",
            select: {
              options: [...yesNoOptions],
            },
          },
        },
        {
          prop: "enabled",
          name: "Enabled",
          pipes: [{ clazz: BoolToStringPipe }],
          inlineEdit: {
            control: "select",
            select: {
              options: [...yesNoOptions],
            },
          },
        },
        {
          prop: "isDefault",
          name: "Default",
          pipes: [{ clazz: BoolToStringPipe }],
          inlineEdit: {
            control: "select",
            select: {
              options: [...yesNoOptions],
            },
          },
        },
      ] as DataTableColumn<MaintenanceProvider>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (mp) => col?.inlineEdit?.disabled?.(mp) || !this.currentUser.hasRight(["admin"]),
        },
      }));

    return {
      id: "mp",
      entityName: "Maintenance Provider",
      syncWithQueryParams: true,
      toolBar: {
        exportCsv: {
          filename: "maintenance-providers",
          data: {
            browser: (queryParams) => this.maintenanceProviderService.getMaintenanceProviders(queryParams),
            email: (queryParams) => this.maintenanceProviderService.getMaintenanceProvidersQueue(queryParams),
          },
        },
      },
      columns,
      defaultColumns: [],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (company) => this.viewMaintenanceProvider(company, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "visibility",
              text: "View",
              function: this.manageMaintenanceProvider,
              hidden: (mp) => actionColumn?.view?.hidden,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteMaintenanceProvider,
              hidden: (mp) => actionColumn?.delete?.hidden || !this.currentUser.isGlobalAdmin(),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getMaintenanceProviderSearchFilterOptions(this.currentUser),
        },
      ],
    };
  };

  /** Populate table */
  getMaintenanceProviders = (event: GetDataEvent<MaintenanceProvider>): void => {
    const { onError, onResult, queryParams } = event;
    this.maintenanceProviderService
      .getMaintenanceProviders({
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data) => {
          onResult(data);
        },
        (error) => {
          onError(error);
        },
      );
  };

  /** Update table */
  update = (event: UpdateDataEvent<MaintenanceProvider>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      this.maintenanceProviderService.updateMaintenanceProvider(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  //#region Private Methods

  private viewMaintenanceProvider = (maintenanceProvider: MaintenanceProvider, config?: DataTableConfig): void => {
    const columns = this.getMaintenanceProviderTableConfig(config).columns;

    const data = columns
      .filter((col) => !col.hidden)
      .map((col) => ({
        name: col.name,
        value: this.dataTableService.parseData(maintenanceProvider, col, get(maintenanceProvider, col.prop)),
      }));

    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private manageMaintenanceProvider = (maintenanceProvider: MaintenanceProvider): void => {
    this.router.navigate([`/maintenance-providers/${maintenanceProvider.id}`]);
  };

  private deleteMaintenanceProvider = (
    maintenanceProvider: MaintenanceProvider,
    table: DataTableComponent<MaintenanceProvider>,
  ): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "maintenanceProvider",
        data: maintenanceProvider,
        url: this.maintenanceProviderService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  //#endregion
}
